/**
 * The account store, the lockout, and the session.
 *
 * Everything lives in this browser: accounts in localStorage, the session in
 * sessionStorage so closing the tab ends it. That is honest about what a
 * client-side gate can be — it stops someone using the screen, it does not
 * protect the data. The security panel says so on every screen.
 */
import { b64u, rnd, pbkdf2, toB32, hashCode } from "./crypto";
import { t } from "../../i18n";

export interface Account {
  email: string;
  salt: string;
  iters: number;
  hash: string;
  secret: string;
  role: string;
  created: string;
  devices: string[];
  tries: number;
  lockUntil: number;
  recovery: string[];
  recoveryUsed: number;
  /** Shown once at enrolment, never stored in the clear. */
  plainRecovery?: string[];
}

export interface Session {
  email: string;
  role: string;
  at: number;
  seen: number;
  device: string;
}

export const GATE = {
  key: "skylogics.accounts",
  sesKey: "skylogics.session",
  idleMin: 30,
  maxTries: 5,
  lockSec: 60,
} as const;

export function accounts(): Account[] {
  try {
    return JSON.parse(localStorage.getItem(GATE.key) || "[]");
  } catch {
    return [];
  }
}

export function saveAccounts(list: Account[]): void {
  try {
    localStorage.setItem(GATE.key, JSON.stringify(list));
  } catch {
    alert(t("This browser will not store the account. The gate cannot be set up here."));
  }
}

export const makeRecovery = (): string[] =>
  Array.from({ length: 10 }, () => toB32(rnd(5)).slice(0, 4) + "-" + toB32(rnd(5)).slice(0, 4));

export async function createAccount(email: string, pass: string, role: string, isFirst: boolean): Promise<Account> {
  const salt = b64u(rnd(16));
  const secret = toB32(rnd(20));
  const recovery = makeRecovery();
  const acc: Account = {
    email: email.toLowerCase().trim(),
    salt,
    iters: 150000,
    hash: await pbkdf2(pass, salt, 150000),
    secret,
    role: role || (isFirst ? "Administrator" : "User"),
    created: new Date().toISOString(),
    devices: [],
    tries: 0,
    lockUntil: 0,
    recovery: await Promise.all(recovery.map((c) => hashCode(c, salt))),
    recoveryUsed: 0,
  };
  const list = accounts();
  list.push(acc);
  saveAccounts(list);
  acc.plainRecovery = recovery; /* shown once, never stored in the clear */
  return acc;
}

export interface PasswordResult {
  ok: boolean;
  why?: string;
  acc?: Account;
}

export async function checkPassword(email: string, pass: string): Promise<PasswordResult> {
  const list = accounts();
  const a = list.find((x) => x.email === email.toLowerCase().trim());
  if (!a) return { ok: false, why: t("No account with that address.") };
  if (a.lockUntil && Date.now() < a.lockUntil)
    return {
      ok: false,
      why: t("Locked for {n} more seconds.", { n: Math.ceil((a.lockUntil - Date.now()) / 1000) }),
    };
  const h = await pbkdf2(pass, a.salt, a.iters || 150000);
  if (h !== a.hash) {
    a.tries = (a.tries || 0) + 1;
    if (a.tries >= GATE.maxTries) {
      a.lockUntil = Date.now() + GATE.lockSec * 1000;
      a.tries = 0;
      saveAccounts(list);
      return {
        ok: false,
        why: t("Too many attempts. Locked for {n} seconds.", { n: GATE.lockSec }),
      };
    }
    saveAccounts(list);
    return {
      ok: false,
      why: t("That password is not right. {n} attempt(s) left.", { n: GATE.maxTries - a.tries }),
    };
  }
  a.tries = 0;
  a.lockUntil = 0;
  saveAccounts(list);
  return { ok: true, acc: a };
}

/** A recovery code works exactly once, and is then spent. */
export async function recoveryValid(email: string, code: string): Promise<boolean> {
  const list = accounts();
  const a = list.find((x) => x.email === email.toLowerCase().trim());
  if (!a || !a.recovery) return false;
  const h = await hashCode(code, a.salt);
  const i = a.recovery.indexOf(h);
  if (i === -1) return false;
  a.recovery.splice(i, 1);
  a.recoveryUsed = (a.recoveryUsed || 0) + 1;
  saveAccounts(list);
  return true;
}

export const deviceId = (): string => {
  let id: string | null = null;
  try {
    id = localStorage.getItem("skylogics.device");
  } catch {
    /* storage refused; a fresh id per load is the honest fallback */
  }
  if (!id) {
    id = b64u(rnd(12));
    try {
      localStorage.setItem("skylogics.device", id);
    } catch {
      /* nothing to do — the device is simply not remembered */
    }
  }
  return id;
};

export interface DemoUser {
  name: string;
  role: string;
}

export function startSession(acc: Account): { session: Session; user: DemoUser } {
  const ses: Session = {
    email: acc.email,
    role: acc.role,
    at: Date.now(),
    seen: Date.now(),
    device: deviceId(),
  };
  try {
    sessionStorage.setItem(GATE.sesKey, JSON.stringify(ses));
  } catch {
    /* the session simply will not survive a reload */
  }
  const user: DemoUser = {
    name: acc.email
      .split("@")[0]
      .replace(/[._]/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase()),
    role: acc.role,
  };
  try {
    localStorage.setItem("skylogics.user", JSON.stringify(user));
  } catch {
    /* as above */
  }
  return { session: ses, user };
}

/** Reading the session also refreshes it; past the idle limit it ends instead. */
export function currentSession(): Session | null {
  try {
    const s: Session | null = JSON.parse(sessionStorage.getItem(GATE.sesKey) || "null");
    if (!s) return null;
    if (Date.now() - s.seen > GATE.idleMin * 60000) {
      endSession();
      return null;
    }
    s.seen = Date.now();
    sessionStorage.setItem(GATE.sesKey, JSON.stringify(s));
    return s;
  } catch {
    return null;
  }
}

export function endSession(): void {
  try {
    sessionStorage.removeItem(GATE.sesKey);
  } catch {
    /* nothing held it anyway */
  }
}
